import { Slot } from 'radix-ui'
import * as React from 'react'
import { cn } from '../../lib/cn'
import type {
  CardContentProps,
  CardDescriptionProps,
  CardFooterProps,
  CardHeaderProps,
  CardProps,
  CardTitleProps,
} from './card.types'

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ asChild = false, className, variant = 'default', ...props }, ref) => {
    const Comp = asChild ? Slot.Slot : 'div'
    return (
      <Comp
        ref={ref}
        data-variant={variant}
        className={cn(
          'group/card flex flex-col gap-(--space-lg) rounded-(--radius-card) p-(--space-xl)',
          'bg-card text-card-foreground',
          'data-[variant=black]:bg-foreground data-[variant=black]:text-background',
          className,
        )}
        {...props}
      />
    )
  },
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ asChild = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot.Slot : 'div'
    return (
      <Comp
        ref={ref}
        className={cn(
          'flex flex-col gap-(--space-2xs) has-[+_[data-slot=card-content]]:border-b has-[+_[data-slot=card-content]]:border-border has-[+_[data-slot=card-content]]:pb-(--space-sm)',
          'group-data-[variant=black]/card:has-[+_[data-slot=card-content]]:border-background/20',
          className,
        )}
        {...props}
      />
    )
  },
)
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ asChild = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot.Slot : 'h4'
    return (
      <Comp
        ref={ref}
        data-slot='card-title'
        // `text-inherit` restores inheritance from the card surface, which a
        // host heading reset (AntD's `h1..h6 { color }`, Ionic's typography.css)
        // otherwise wins over by declaring on the element. Inheriting is what
        // keeps the `black` variant's `text-background` reaching the title.
        className={cn('cs-type-h4 uppercase my-0 text-inherit', className)}
        {...props}
      />
    )
  },
)
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  CardDescriptionProps
>(({ asChild = false, className, ...props }, ref) => {
  const Comp = asChild ? Slot.Slot : 'p'
  return (
    <Comp
      ref={ref}
      data-slot='card-description'
      className={cn(
        'cs-type-caption text-muted-foreground my-0',
        'group-data-[variant=black]/card:text-background/70',
        className,
      )}
      {...props}
    />
  )
})
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ asChild = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot.Slot : 'div'
    return (
      <Comp
        ref={ref}
        data-slot='card-content'
        className={cn(' ', className)}
        {...props}
      />
    )
  },
)
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ asChild = false, className, ...props }, ref) => {
    const Comp = asChild ? Slot.Slot : 'div'
    return (
      <Comp
        ref={ref}
        className={cn('flex items-center ', className)}
        {...props}
      />
    )
  },
)
CardFooter.displayName = 'CardFooter'

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle }
